import {clamp, mapValues, range, values} from 'remeda'
import {sbServer, ChartRow} from 'utils/db'
import {match, P} from 'ts-pattern'
import type {GetStaticPaths, GetStaticProps} from 'next'
import {styles, colors} from 'components/theme/theme'
import {useState} from 'react'
import {textInMap} from 'components/embed/ukr-nuke/ukraineMap'
import UkraineMap from 'components/embed/ukr-nuke/UkraineMap'
import {keyBy, mapEntriesToObj} from 'shared/collection'
import {notNull} from 'shared/type'
import {compactObj, keys} from 'shared/collection'
import {useWindowDim} from 'shared/ui'
import {
  useDebouncedSize,
  ForecastBar,
  forecastBarMeta,
  ForecastBarCfg,
  ForecastBarProps,
  causeMeta,
  CausesCfg,
  CausesProps,
  Causes,
  consequencesMeta,
  ConsequencesCfg,
  ConsequencesProps,
  Consequences
} from 'shared/chart/v1'
import {CraftedChartPage} from 'components/charts/CraftedChart'

const questionColor = colors.amber[4]
const {edit: causesEdit} = causeMeta()
const {edit: consequencesEdit} = consequencesMeta()

type PageProps = {
  charts: ChartRow[]
  section?: Section
  causes?: string[]
  consequences?: string[]
}
export default function EuNuke2013({section, charts: rawCharts, causes, consequences}: PageProps) {
  const byId = keyBy(rawCharts ?? [], c => c.id)
  const charts = mapValues(nukeCharts, id => byId[id])
  const [causesText, setCausesText] = useState<string>()
  const shouldShow = (s: Section) => section == null || s == section
  const embedded = section != null
  const {ref: causesDiv, width: causesDivWidth} = useDebouncedSize({debounce: 10})
  const windowDim = useWindowDim()
  const causesProps =
    charts.causes && (causesEdit.cfgToProps(charts.causes?.chart_cfg as CausesCfg) as CausesProps['chart'])
  const consequencesChart =
    charts.consequences &&
    shouldShow('consequences') &&
    (consequencesEdit.cfgToProps(charts.consequences?.chart_cfg as ConsequencesCfg) as ConsequencesProps['chart'])
  const mapText = causesProps?.causes
    .filter(c => causes == null || causes.includes(c.causeCode!))
    .map(c => c.title ?? '')
  const mapRelevant = textInMap(mapText ?? [])
  const mapToSide = causesDivWidth > 800 && textInMap

  return (
    <CraftedChartPage
      embedded={embedded}
      titleShort='European nuclear risk'
      title='Will a nuclear weapon be detonated in Europe as an act of hostility before the 30th April 2023?'>
      {charts.question && shouldShow('question') && (
        <>
          <ForecastBar
            chart={
              forecastBarMeta.edit.cfgToProps(charts.question?.chart_cfg as ForecastBarCfg) as ForecastBarProps['chart']
            }
          />
          <p className='mb-10 text-center'>
            The equivalent risk for a{' '}
            <span style={{color: questionColor}}>
              30 day period is <b className=''>1.3%</b>
            </span>
            , which will be used to compare with other forecasts.
          </p>
        </>
      )}

      {charts.questionV2 && shouldShow('questionV2') && (
        <>
          {!embedded && <div className='mt-20'>We re-forecast the original question again (Nov 2nd)</div>}
          <ForecastBar
            chart={
              forecastBarMeta.edit.cfgToProps(
                charts.questionV2?.chart_cfg as ForecastBarCfg
              ) as ForecastBarProps['chart']
            }
          />
        </>
      )}

      {shouldShow('causes') && (
        <>
          {!embedded && (
            <h2 className='mt-[3em]'>What are the chances of a detonation in the 30 days following these events:</h2>
          )}
          <div
            ref={causesDiv}
            className='flex'
            style={{
              ...(embedded ? {} : styles.container.fullWidth),
              flexDirection: mapToSide ? 'row' : 'column'
            }}>
            <div style={mapToSide ? {width: causesDivWidth * 0.5} : {}}>
              {causesProps && (
                <Causes chart={causesProps} onHover={c => setCausesText(c?.title)} causeFilter={causes} />
              )}
            </div>
            {mapRelevant && (
              <div className={mapToSide ? 'grow' : 'mt-10'}>
                <div className={embedded ? '' : 'sticky top-5'}>
                  <UkraineMap
                    hoverText={causesText}
                    contextText={mapText}
                    height={mapToSide && windowDim.h ? clamp(windowDim.h, {min: 300, max: 900}) : 400}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {consequencesChart && (
        <Consequences
          chart={{...consequencesChart, ...(embedded ? {title: '', subTitle: ''} : {})}}
          groupFilter={consequences}
          className={`${embedded ? '' : 'mt-[5em]'}`}
          showTitles={!embedded || consequences?.length != 1}
        />
      )}

      {/* <ChartView className='pt-5' row={charts.outcomes} /> */}
    </CraftedChartPage>
  )
}

const nukeCharts = {
  question: '05325b37-368b-49f7-ac66-f33fa9f03b3e',
  questionV2: '61e9d69a-f6e6-486d-aa8b-2ff3255a6f26',
  causes: '24507e2b-778a-477a-b10f-fb39f6fa4503',
  consequences: 'c1bdc10e-4177-4e08-9aa0-2a46a631bb99'
} as const
const allSections = keys(nukeCharts)
type Section = (typeof allSections)[number]
const chartIdToSection = mapEntriesToObj(nukeCharts, (k, v) => [v, k])

export const nukeEmbedInvalidationPaths = (chartId: string) => {
  const section = (chartIdToSection as Record<string, Section>)[chartId as Section]
  if (!section) return []
  return getPaths([section]).map(p => ['/embed/eu-nuke-2023', ...p.params.slug].join('/'))
}

export const getStaticPaths: GetStaticPaths = () => ({
  paths: getPaths(),
  fallback: true
})

const articleCauses = ['1|2|7|10', '5|9', '3|11', '4', '13|14', '8|12|6', '15|16']
const articleConsequences = range(1, 15).map(i => i.toString())
const getPaths = (sections?: Section[]) => {
  const _sections = sections ?? allSections
  const makePath = (section: Section | null, id?: string) => ({
    params: {slug: [section, id].filter(notNull)}
  })
  const paths = [
    ...[null, ..._sections].map(s => makePath(s as Section | null)),
    ...(_sections.includes('causes') ? articleCauses.map(c => makePath('causes', c)) : []),
    ...(_sections.includes('consequences') ? articleConsequences.map(c => makePath('consequences', c)) : [])
  ]
  return paths
}

export const getStaticProps: GetStaticProps<PageProps, {slug: string[]}> = async ({params}) => {
  if (!sbServer) return {notFound: true}
  const props = compactObj(
    match(params?.slug)
      .with([P.string], ([section]) => ({section: section as Section}))
      .with([P.string, P.string], ([sectionRaw, slugItems]) => {
        const items = slugItems?.split('|')
        const section = sectionRaw as Section
        return {section, [section]: items}
      })
      .otherwise(() => ({}))
  ) as Partial<PageProps>

  let query = sbServer.from('chart').select('*')
  if (props.section != null) query = query.eq('id', nukeCharts[props.section])
  else query = query.in('id', values(nukeCharts))
  const {data: charts} = await query
  if (!charts?.length) return {notFound: true}
  return {props: {...props, charts}}
}
